import { useState, useEffect, useCallback } from "react";
import {
  Package, Plus, Save, Trash2, Edit2, X, RefreshCw,
  ChevronDown, ChevronUp, Search, Filter, Download
} from "lucide-react";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function CatalogueDevisManager({ societeId }) {
  const [catalogue, setCatalogue] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [expandedSections, setExpandedSections] = useState({});
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: "", description: "", marque: "", section: "", unite: "Unite", prix_ht: 0, tva_rate: 10.0, section_order: 0, order: 0 });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    if (!societeId) return;
    setLoading(true);
    try {
      const r = await authFetch(`${API}/societes/${societeId}/catalogue`);
      if (r.ok) {
        const data = await r.json();
        setCatalogue(data.items || []);
        setSections(data.sections || []);
        const exp = {};
        (data.sections || []).forEach(s => { exp[s] = true; });
        setExpandedSections(prev => Object.keys(prev).length ? prev : exp);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [societeId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editItem
        ? `${API}/societes/${societeId}/catalogue/${editItem}`
        : `${API}/societes/${societeId}/catalogue`;
      const r = await authFetch(url, {
        method: editItem ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, type_devis: "mpa" }),
      });
      if (r.ok) {
        setShowForm(false);
        setEditItem(null);
        load();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await authFetch(`${API}/societes/${societeId}/catalogue/${id}`, { method: "DELETE" });
    load();
  };

  const handleImportReceveurs = async () => {
    if (!window.confirm("Importer les receveurs depuis RECEVEURS BIS ?\nLes receveurs precedemment importes seront remplaces.")) return;
    setImporting(true);
    try {
      const r = await authFetch(`${API}/societes/${societeId}/catalogue/import-receveurs-bis`, { method: "POST" });
      if (r.ok) {
        const data = await r.json();
        alert(`${data.imported} receveurs importes avec succes !`);
        load();
      } else {
        const err = await r.json();
        alert(`Erreur: ${err.detail || "Import echoue"}`);
      }
    } catch (e) {
      console.error(e);
      alert("Erreur reseau lors de l'import");
    }
    setImporting(false);
  };

  const handleImportParois = async () => {
    if (!window.confirm("Importer les parois depuis PAROIS BIS ?\nLes parois precedemment importees seront remplacees.")) return;
    setImporting(true);
    try {
      const r = await authFetch(`${API}/societes/${societeId}/catalogue/import-parois-bis`, { method: "POST" });
      if (r.ok) {
        const data = await r.json();
        alert(`${data.imported} parois importees avec succes !`);
        load();
      } else {
        const err = await r.json();
        alert(`Erreur: ${err.detail || "Import echoue"}`);
      }
    } catch (e) {
      console.error(e);
      alert("Erreur reseau lors de l'import");
    }
    setImporting(false);
  };

  const grouped = {};
  catalogue
    .filter(i => (!search || i.nom.toLowerCase().includes(search.toLowerCase()) || i.description.toLowerCase().includes(search.toLowerCase())))
    .filter(i => (!filterSection || i.section === filterSection))
    .forEach(i => {
      if (!grouped[i.section]) grouped[i.section] = [];
      grouped[i.section].push(i);
    });

  const formatPrice = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);

  if (!societeId) return <div className="text-center py-8 text-slate-400">Selectionnez une societe</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-orange-400" />
            Catalogue Produits / Prestations MPA
          </h3>
          <p className="text-xs text-slate-400">{catalogue.length} produits</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="import-receveurs-btn"
            onClick={handleImportReceveurs}
            disabled={importing}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-sm"
          >
            {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {importing ? "Import..." : "Receveurs"}
          </button>
          <button
            data-testid="import-parois-btn"
            onClick={handleImportParois}
            disabled={importing}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm"
          >
            {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {importing ? "Import..." : "Parois"}
          </button>
          <button
            data-testid="add-catalogue-item-btn"
            onClick={() => { setForm({ nom: "", description: "", marque: "", section: sections[0] || "", unite: "Unite", prix_ht: 0, tva_rate: 10.0, section_order: 0, order: 0 }); setEditItem(null); setShowForm(true); }}
            className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" /> Ajouter
          </button>
          <button onClick={load} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="flex-1 flex items-center gap-2 bg-slate-800 rounded-lg border border-slate-700 px-3 py-2">
          <Search className="w-4 h-4 text-slate-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="bg-transparent w-full text-sm text-white outline-none" />
        </div>
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg border border-slate-700 px-3 py-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <select value={filterSection} onChange={e => setFilterSection(e.target.value)} className="bg-transparent text-sm text-white outline-none">
            <option value="">Toutes sections</option>
            {sections.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-white text-sm">{editItem ? "Modifier" : "Ajouter un produit"}</h4>
            <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-700 rounded"><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Nom</label>
              <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Section</label>
              <select value={form.section} onChange={e => setForm(f => ({ ...f, section: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white">
                {sections.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-400">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white h-16" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Marque</label>
              <input value={form.marque} onChange={e => setForm(f => ({ ...f, marque: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs text-slate-400">Prix HT</label>
                <input type="number" step="0.01" value={form.prix_ht} onChange={e => setForm(f => ({ ...f, prix_ht: parseFloat(e.target.value) || 0 }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div className="w-24">
                <label className="text-xs text-slate-400">Unite</label>
                <select value={form.unite} onChange={e => setForm(f => ({ ...f, unite: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white">
                  <option value="Unite">Unite</option>
                  <option value="m2">m2</option>
                  <option value="ml">ml</option>
                  <option value="Forfait">Forfait</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 bg-slate-700 rounded-lg text-sm">Annuler</button>
            <button data-testid="save-catalogue-item-btn" onClick={handleSave} disabled={saving || !form.nom} className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white rounded-lg text-sm">
              {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {editItem ? "Modifier" : "Ajouter"}
            </button>
          </div>
        </div>
      )}

      {/* Products by section */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-8 text-slate-500">Aucun produit dans le catalogue</div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).sort(([,a],[,b]) => (a[0]?.section_order || 0) - (b[0]?.section_order || 0)).map(([section, items]) => (
            <div key={section} className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedSections(p => ({ ...p, [section]: !p[section] }))}
                className="w-full flex items-center justify-between p-3 hover:bg-slate-700/30 transition-colors"
              >
                <span className="text-sm font-semibold text-orange-400">{section}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{items.length} produits</span>
                  {expandedSections[section] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </button>
              {expandedSections[section] && (
                <div className="border-t border-slate-700/50">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-slate-700/30">
                        <th className="text-left px-3 py-2">Produit</th>
                        <th className="text-left px-3 py-2">Marque</th>
                        <th className="text-right px-3 py-2">PU HT</th>
                        <th className="text-right px-3 py-2">PU TTC</th>
                        <th className="text-center px-3 py-2">Unite</th>
                        <th className="w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.sort((a, b) => a.order - b.order).map(item => (
                        <tr key={item.id} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                          <td className="px-3 py-2">
                            <div className="text-white">{item.nom}</div>
                            {item.description && <div className="text-xs text-slate-500 truncate max-w-md">{item.description}</div>}
                          </td>
                          <td className="px-3 py-2 text-slate-400 text-xs">{item.marque || '-'}</td>
                          <td className="px-3 py-2 text-right text-white font-mono">{formatPrice(item.prix_ht)}</td>
                          <td className="px-3 py-2 text-right text-slate-400 font-mono">{formatPrice(item.prix_ht * (1 + item.tva_rate / 100))}</td>
                          <td className="px-3 py-2 text-center text-slate-400">{item.unite}</td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => { setForm(item); setEditItem(item.id); setShowForm(true); }} className="p-1 hover:bg-slate-600 rounded"><Edit2 className="w-3 h-3 text-blue-400" /></button>
                              <button onClick={() => handleDelete(item.id)} className="p-1 hover:bg-red-900/50 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
